/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Redirect, Route, Switch, useLocation } from 'react-router';
import { NotFoundRedirect } from 'src/core/components/NotFoundRoute';
import { Page } from 'src/core/components/Page';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { LanguageStudioSubHeader } from 'src/languageStudio/appShell/landingPage/components/LanguageStudioSubHeader';
import { CallCenterDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/callCenterDemo/CallCenterDemoPage';
import { DetectLanguageDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/DetectLanguageDemoPage';
import { GenerateQuestionsDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/generateQuestionsDemo/GenerateQuestionsDemoPage';
import { KeyPhrasesDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/keyPhrasesDemo/KeyPhrasesDemoPage';
import { LinkedEntitiesDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/linkedEntitiesDemo/LinkedEntitiesDemoPage';
import { NamedEntityDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/NamedEntityDemoPage';
import { PIIDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/PIIDemoPage';
import { QuestionAnswersDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/questionAnswersDemo/QuestionAnswersDemoPage';
import { SentimentDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/sentimentDemo/SentimentDemoPage';
import { SummarizationDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/summarizationDemo/SummarizationDemoPage';
import { TaForHealthDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/taForHealth/TaForHealthDemoPage';
import { DocumentTranslationDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/translatorDemos/DocumentTranslationDemoPage';
import { TextTranslationDemoPage } from 'src/languageStudio/appShell/prebuiltDemos/translatorDemos/TextTranslationDemoPage';
import { pageClass } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';

const messages = defineMessages({
    piiPageTitle: { id: 'languageStudio.demoPage.piiPageTitle', defaultMessage: 'Language Studio: PII tryout' },
    keyPhrasesPageTitle: { id: 'languageStudio.demoPage.keyPhrasesPageTitle', defaultMessage: 'Language Studio: Key phrases tryout' },
    namedEntitiesPageTitle: {
        id: 'languageStudio.demoPage.namedEntitiesPageTitle',
        defaultMessage: 'Language Studio: Named entities tryout'
    },
    sentimentPageTitle: {
        id: 'languageStudio.demoPage.sentimentPageTitle',
        defaultMessage: 'Language Studio: Sentiment and opinion mining tryout'
    },
    detectLanguagePageTitle: {
        id: 'languageStudio.demoPage.detectLanguagePageTitle',
        defaultMessage: 'Language Studio: Detect language tryout'
    },
    questionAnsweringPageTitle: {
        id: 'languageStudio.demoPage.questionAnsweringPageTitle',
        defaultMessage: 'Language  studio: Question answering tryout'
    },
    linkedEntitiesPageTitle: {
        id: 'languageStudio.demoPage.linkedEntitiesPageTitle',
        defaultMessage: 'Language Studio: Linked entities tryout'
    },
    textTranslationPageTitle: {
        id: 'languageStudio.demoPage.textTranslationPageTitle',
        defaultMessage: 'Language Studio: Translate text tryout'
    },
    documentTranslationPageTitle: {
        id: 'languageStudio.demoPage.documentTranslationPageTitle',
        defaultMessage: 'Language Studio: Translate documents tryout'
    },
    taForHealthPageTitle: {
        id: 'languageStudio.demoPage.taForHealthPageTitle',
        defaultMessage: 'Language Studio: Extract health information tryout'
    },
    summarizationPageTitle: {
        id: 'languageStudio.demoPage.summarizationPageTitle',
        defaultMessage: 'Language Studio: Summarization information tryout'
    },
    callCenterPageTitle: {
        id: 'languageStudio.demoPage.callCenterPageTitle',
        defaultMessage: 'Language Studio: Call center tryout'
    }
});

const { styleDiv } = getStylist('DemoPage');
const ScrollElement = styleDiv('ScrollElement', { marginTop: '0 !important' });

/**
 * Displays a demo page with all demos pages routes.
 */
export const DemoPage = () => {
    const { pathname } = useLocation();
    const { localize } = useLocalization();

    let title: string = null;
    let pageName = 'DemoPage';

    const scrollElementRef = React.useRef<HTMLDivElement>();

    React.useEffect(() => {
        scrollElementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [pathname]);

    switch (pathname) {
        case routeTable.languageStudio.namedEntitiesDemoPage():
            pageName = 'NamedEntityDemoPage';
            title = localize(messages.namedEntitiesPageTitle);
            break;
        case routeTable.languageStudio.sentimentDemoPage():
            pageName = 'SentimentDemoPage';
            title = localize(messages.sentimentPageTitle);
            break;
        case routeTable.languageStudio.detectLanguageDemoPage():
            pageName = 'DetectLanguageDemoPage';
            title = localize(messages.detectLanguagePageTitle);
            break;
        case routeTable.languageStudio.questionAnswersDemoPage():
            pageName = 'QuestionAnswersDemoPage';
            title = localize(messages.questionAnsweringPageTitle);
            break;
        case routeTable.languageStudio.piiDemoPage():
            pageName = 'PIIDemoPage';
            title = localize(messages.piiPageTitle);
            break;
        case routeTable.languageStudio.keyPhrasesDemoPage():
            pageName = 'KeyPhrasesDemoPage';
            title = localize(messages.keyPhrasesPageTitle);
            break;
        case routeTable.languageStudio.linkedEntitiesDemoPage():
            pageName = 'LinkedEntitiesDemoPage';
            title = localize(messages.linkedEntitiesPageTitle);
            break;
        case routeTable.languageStudio.taForHealthDemoPage():
            pageName = 'TaForHealthDemoPage';
            title = localize(messages.taForHealthPageTitle);
            break;
        case routeTable.languageStudio.textTranslationDemoPage():
            pageName = 'TextTranslationDemoPage';
            title = localize(messages.textTranslationPageTitle);
            break;
        case routeTable.languageStudio.documentTranslationDemoPage():
            pageName = 'DocumentTranslationDemoPage';
            title = localize(messages.documentTranslationPageTitle);
            break;
        case routeTable.languageStudio.summarizationDemoPage():
            pageName = 'SummarizationDemoPage';
            title = localize(messages.summarizationPageTitle);
            break;
        case routeTable.languageStudio.callCenterDemoPage():
            pageName = 'CallCenterDemoPage';
            title = localize(messages.callCenterPageTitle);
            break;
        default:
    }

    return (
        <>
            <LanguageStudioSubHeader marginLeft="9.5%" pageKey={pageName} pageName={title?.split(':')[1].substring(1)} />

            <Page title={title} size="container" pageName={pageName} className={pageClass}>
                <ScrollElement originalRef={scrollElementRef} />

                <Switch>
                    <Route exact path={routeTable.languageStudio.piiDemoPage()} component={PIIDemoPage} />
                    <Route exact path={routeTable.languageStudio.sentimentDemoPage()} component={SentimentDemoPage} />
                    <Route exact path={routeTable.languageStudio.callCenterDemoPage()} component={CallCenterDemoPage} />
                    <Route exact path={routeTable.languageStudio.keyPhrasesDemoPage()} component={KeyPhrasesDemoPage} />
                    <Route exact path={routeTable.languageStudio.taForHealthDemoPage()} component={TaForHealthDemoPage} />
                    <Route exact path={routeTable.languageStudio.namedEntitiesDemoPage()} component={NamedEntityDemoPage} />
                    <Route exact path={routeTable.languageStudio.summarizationDemoPage()} component={SummarizationDemoPage} />
                    <Route exact path={routeTable.languageStudio.detectLanguageDemoPage()} component={DetectLanguageDemoPage} />
                    <Route exact path={routeTable.languageStudio.linkedEntitiesDemoPage()} component={LinkedEntitiesDemoPage} />
                    <Route exact path={routeTable.languageStudio.textTranslationDemoPage()} component={TextTranslationDemoPage} />
                    <Route exact path={routeTable.languageStudio.questionAnswersDemoPage()} component={QuestionAnswersDemoPage} />
                    <Route exact path={routeTable.languageStudio.generateQuestionsDemoPage()} component={GenerateQuestionsDemoPage} />
                    <Route exact component={DocumentTranslationDemoPage} path={routeTable.languageStudio.documentTranslationDemoPage()} />

                    <Redirect exact from={routeTable.languageStudio.demos()} to={routeTable.languageStudio.home()} />
                    <NotFoundRedirect />
                </Switch>
            </Page>
        </>
    );
};
